/**
 * export-id-plan.js — the one place export ids come from (Workstream A).
 *
 * `buildExportIdPlan(state, opts)` computes the complete renaming plan for an
 * export: root export ids, descendant-id renames, anchor targets, and
 * findings. The SAME plan object feeds the flat exporter, the 11ty exporter,
 * the saved-preview bundler, and preflight — no consumer reimplements any of
 * it. `assignExportIds` in generators.js is a thin delegate.
 *
 * Two modes:
 *
 * LEGACY (`canonicalIds: false`, the default): reproduces the historical
 * `assignExportIds` behavior bug-for-bug — including the catalogKey-keyed
 * dedupe quirk that lets two catalogKeys sharing one plain-slug sectionId
 * emit duplicate root ids in flat mode. Byte-neutrality of the A1–A5 phases
 * is proven against this mode; the quirk dies at the A6 flip, not before.
 *
 * CANONICAL (`canonicalIds: true`): category-derived semantic roots
 * (`hero`, `services`, `contact`, `navigation`, `footer`) with deterministic
 * `-2`/`-3` suffixing, plus descendant-id claims.
 *
 * Root allocation (canonical):
 *   base = EXPORT_BASE_BY_CATEGORY[section.category] || sanitized category
 *          || sanitized sectionId stem || 'section'
 *   Suffix counters advance in site document order (nav → pages in order →
 *   footer, sections in page order) and dedupe on the FINAL id site-wide.
 *   Site-wide (not per-page) uniqueness is deliberate: both exporters emit
 *   site-wide CSS (`local.css`, `:is()` fan-out), so two different sections
 *   sharing `#services` on different pages would collide in the cascade.
 *
 * Descendant allocation (canonical): in the same site document order, the
 * first claimant of an authored descendant id keeps it; every later claimant
 * gets `<rootExportId>-<authoredId>` (then `-2`, `-3` on the rare collision).
 * User-authored ids (present in instance HTML, absent from the instance's
 * `idProvenance.sourceOwnedIds`) claim FIRST and are never renamed. Instances
 * with no valid idProvenance (legacy saves) are conservative: every id they
 * declare is preserved verbatim and claimed, and the ambiguity is disclosed
 * in `findings`. `documentScope` sections (the 9008 modal set) are exempt —
 * their ids are a cross-section cooperation contract.
 *
 * Determinism: every input is state-derived; iteration follows state
 * traversal order; no wall clock, no randomness. Run-twice byte equality
 * (export-lock-tests.js) holds by construction.
 */

import { sectionUsesDocumentScope } from '../shared/section-js-facade.js';
import { isValidIdProvenance, collectDeclaredIds } from '../shared/id-provenance.js';
import { makeFinding, SEVERITY } from '../shared/audit-checks.js';

/**
 * THE canonical-ids switch. Every export surface — flat, 11ty, saved preview —
 * reads this one constant, so the scheme is decided in a single place.
 *
 * FLIPPED TRUE at Workstream A6 (2026-08-17): exports now emit category-derived
 * semantic roots (`hero`, `services`, `navigation`, `footer`, …) instead of the
 * three inherited id vocabularies. The lock manifest records `idScheme: 2` so a
 * shipped site's next re-export diff is machine-explainable.
 *
 * Setting it back to `false` restores the historical ids bug-for-bug (the
 * legacy path is retained and still unit-tested in both modes) — but that is a
 * scheme change in its own right and needs the same gate sequence, including an
 * EXPORTER_CORE_VERSION bump.
 */
export const CANONICAL_EXPORT_IDS = true;

/**
 * Resolve an anchor target through the plan: same-page claimant preferred,
 * else first claimant in document order. Returns the final id or null when
 * the plan says nothing about `oldId`.
 */
export function resolveAnchorId(anchorTargets, oldId, pageId = null) {
  const claimants = anchorTargets instanceof Map ? anchorTargets.get(oldId) : null;
  if (!claimants || claimants.length === 0) return null;
  if (pageId != null) {
    const samePage = claimants.find((c) => c.pageId === pageId);
    if (samePage) return samePage.newId;
  }
  return claimants[0].newId;
}

/**
 * A7 — translate the plan's own findings into the shared audit vocabulary so the
 * export confidence report can show them. Before this, the plan recorded exactly
 * why it had declined to rename something and then dropped it on the floor: an
 * operator reading a scheme-2 diff had no way to tell a deliberately-conservative
 * legacy instance from a bug.
 *
 * Deterministic by construction — the input findings are produced in state
 * traversal order and nothing here consults a clock, a random source, or the
 * environment. `id` is unique per finding because the report dedupes on it.
 */
export function describeIdPlanFindings(plan) {
  if (!plan?.canonicalIds) return [];
  const out = [];
  const push = (id, severity, title, detail) =>
    out.push(makeFinding({ checkId: 'export-ids', id, severity, title, detail }));

  const legacyInstances = [];
  for (const finding of plan.findings || []) {
    switch (finding.type) {
      case 'legacy-id-provenance':
        // Not surfaced one-per-instance: a project saved before A-provenance
        // landed produces one of these for EVERY section, which would bury the
        // real warnings under noise. Collapsed into a single count below.
        legacyInstances.push(finding.instanceId);
        break;
      case 'user-id-collision':
        push(
          `user-id-collision:${finding.id}`,
          SEVERITY.WARNING,
          `Two sections both use the id "${finding.id}"`,
          'Both ids were authored by you, so neither was renamed. Duplicate ids on one page ' +
            'break in-page links and querySelector lookups — rename one in the element panel.'
        );
        break;
      case 'legacy-id-duplicate':
        push(
          `legacy-id-duplicate:${finding.id}`,
          SEVERITY.WARNING,
          `The id "${finding.id}" appears in more than one section`,
          'These sections predate id provenance tracking, so their ids were preserved rather ' +
            'than renamed. Re-inserting one of the sections lets the exporter resolve the clash.'
        );
        break;
      case 'unparseable-section-html':
        push(
          `unparseable-section-html:${finding.instanceId}`,
          SEVERITY.WARNING,
          'A section’s HTML could not be parsed for id planning',
          `Section ${finding.instanceId} shipped with its ids untouched. If its anchors or ` +
            'labels misbehave in the export, that section is where to look first.'
        );
        break;
      default:
        break;
    }
  }

  if (legacyInstances.length) {
    push(
      'legacy-id-provenance',
      SEVERITY.CLEANUP,
      `${legacyInstances.length} section(s) kept their original ids`,
      'These sections were added before the exporter recorded which ids came from the ' +
        'template, so it cannot prove a rename is safe and deliberately renames nothing. ' +
        'They export correctly; swapping or re-adding a section refreshes its provenance.'
    );
  }

  let renamed = 0;
  for (const m of plan.descendantRenames?.values() || []) renamed += m.size;
  if (renamed) {
    push(
      'descendant-renames',
      SEVERITY.CLEANUP,
      `${renamed} nested id(s) renamed to keep each page unique`,
      'Repeated sections share the ids their template author wrote. Later copies were given ' +
        'prefixed ids; links, labels, ARIA references and CSS were updated to match.'
    );
  }

  return out;
}

/** Ids the plan renamed somewhere (input to the composed-output validator). */
export function collectRenamedAwayIds(plan) {
  const renamed = new Set();
  if (!plan) return renamed;
  for (const m of plan.descendantRenames?.values() || []) {
    for (const oldId of m.keys()) renamed.add(oldId);
  }
  for (const [oldId, claimants] of plan.anchorTargets?.entries() || []) {
    if (claimants.some((c) => c.newId !== oldId)) renamed.add(oldId);
  }
  return renamed;
}

/**
 * Ids the plan KNOWINGLY left duplicated (input to the composed-output validator).
 *
 * Two passes deliberately decline to rename and report a warning instead:
 * `user-id-collision` (both ids were authored by the user, so neither is the
 * exporter's to take) and `legacy-id-duplicate` (sections that predate id
 * provenance, where a rename cannot be proven safe). Both are surfaced in the
 * confidence report and both are survivable — the page renders, in-page links
 * to that id resolve to the first match.
 *
 * Without this set the composed-output validator saw the resulting duplicate,
 * could not tell it from an exporter BUG, and threw — so a project containing
 * one legacy section became unexportable, blocked by the very condition the
 * plan had just told the user was a warning. The gate keeps its teeth: a
 * duplicate the plan did NOT predict still fails the export.
 */
export function collectKnownDuplicateIds(plan) {
  const known = new Set();
  for (const finding of plan?.findings || []) {
    if (finding?.type === 'user-id-collision' || finding?.type === 'legacy-id-duplicate') {
      if (finding.id) known.add(finding.id);
    }
  }
  return known;
}

const TEMPLATE_SECTION_ID_PREFIX_RE = /^tpl-[a-z0-9-]+-/;
const TEMPLATE_DOMAIN_TLDS = new Set([
  'com',
  'net',
  'org',
  'io',
  'co',
  'us',
  'ca',
  'uk',
  'biz',
  'info',
  'dev',
  'app',
  'ai'
]);

/**
 * Friendlier export bases for category names that read awkwardly as element
 * ids. Categories not listed export as themselves (sanitized).
 */
export const EXPORT_BASE_BY_CATEGORY = {
  'forms-contact': 'contact',
  'interior-pages': 'interior',
  'meet-the-team': 'team',
  'special-codestitches': 'special',
  'content-flair': 'flair',
  'side-by-side': 'side-by-side'
};

/**
 * Deterministic CSS-ident-safe id sanitizer. Rules (all unit-tested):
 * NFKD-fold to ASCII, lowercase, non [a-z0-9_-] runs → '-', collapse/trim
 * '-', 's-' prefix for a leading digit, max length 40 truncated at a '-'
 * boundary where possible, empty → 'section'.
 */
export function sanitizeExportId(raw) {
  let s = String(raw ?? '');
  s = s.normalize ? s.normalize('NFKD') : s;
  s = s
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (!s) return 'section';
  if (/^\d/.test(s)) s = `s-${s}`;
  if (s.length > 40) {
    const cut = s.slice(0, 40);
    const lastDash = cut.lastIndexOf('-');
    s = lastDash > 20 ? cut.slice(0, lastDash) : cut;
  }
  return s;
}

/** Historical tpl-<site>- prefix stripper (verbatim from generators.js). */
export function normalizeTemplateSectionId(sectionId) {
  if (typeof sectionId !== 'string') return sectionId;
  if (!sectionId.startsWith('tpl-')) return sectionId;

  const parts = sectionId.split('-');
  if (parts.length < 3) {
    return sectionId.replace(TEMPLATE_SECTION_ID_PREFIX_RE, '');
  }

  const tldIndex = parts.findIndex((part, idx) => idx >= 2 && TEMPLATE_DOMAIN_TLDS.has(part));
  const start = tldIndex >= 2 && tldIndex + 1 < parts.length ? tldIndex + 1 : 2;
  const candidate = parts.slice(start).join('-');
  return candidate || sectionId.replace(TEMPLATE_SECTION_ID_PREFIX_RE, '');
}

function collectAllSectionInstances(state) {
  const allInstances = [];
  if (state?.sharedSections?.navigation) {
    allInstances.push({
      section: state.sharedSections.navigation,
      pageId: null,
      shared: 'navigation'
    });
  }
  for (const page of state?.pages || []) {
    for (const section of page?.sections || []) {
      allInstances.push({ section, pageId: page.id ?? null, shared: null });
    }
  }
  if (state?.sharedSections?.footer) {
    allInstances.push({ section: state.sharedSections.footer, pageId: null, shared: 'footer' });
  }
  return allInstances;
}

/** Legacy root allocation — MUST stay bug-for-bug with historic assignExportIds. */
function assignLegacyRootIds(entries, normalizeTemplateIds) {
  const exportIdMap = new Map();
  const catalogCount = new Map();
  for (const { section } of entries) {
    const rawSectionId = section.sectionId || section.catalogKey || section.instanceId || 'section';
    const normalizedSectionId = normalizeTemplateIds
      ? normalizeTemplateSectionId(rawSectionId)
      : rawSectionId;
    const exportSectionId = normalizedSectionId || rawSectionId;
    const key = normalizeTemplateIds ? exportSectionId : section.catalogKey || rawSectionId;
    const count = (catalogCount.get(key) || 0) + 1;
    catalogCount.set(key, count);
    exportIdMap.set(
      section.instanceId,
      count === 1 ? exportSectionId : `${exportSectionId}-${count}`
    );
  }
  return exportIdMap;
}

function canonicalBaseFor(section, shared) {
  if (shared) return shared; // 'navigation' | 'footer'
  const category = typeof section.category === 'string' ? section.category.trim() : '';
  if (category && category !== 'unknown') {
    return EXPORT_BASE_BY_CATEGORY[category] || sanitizeExportId(category);
  }
  // Deliberately NOT falling through to instanceId: it is a random-looking
  // internal handle, and 'section' is the honest empty fallback.
  const stem = normalizeTemplateSectionId(section.sectionId || section.catalogKey || '');
  return sanitizeExportId(stem);
}

/** Canonical root allocation — dedupe on the FINAL id, site-wide. */
function assignCanonicalRootIds(entries) {
  const exportIdMap = new Map();
  const taken = new Map(); // base → count
  for (const entry of entries) {
    const base = canonicalBaseFor(entry.section, entry.shared);
    const count = (taken.get(base) || 0) + 1;
    taken.set(base, count);
    exportIdMap.set(entry.section.instanceId, count === 1 ? base : `${base}-${count}`);
  }
  return exportIdMap;
}

function defaultParse(html) {
  if (typeof DOMParser === 'undefined') return null;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    return doc.body.firstElementChild;
  } catch {
    return null;
  }
}

/**
 * Compute descendant-id claims and renames for canonical mode.
 * Claim priority: (1) user-authored ids across all instances, (2) ids of
 * legacy-provenance instances (preserved conservatively), (3) template ids in
 * document order. Later claimants of a taken id rename to
 * `<rootExportId>-<authoredId>` (suffix loop on collision).
 */
function computeDescendantPlan(entries, exportIdMap, parseHTML, findings) {
  const descendantRenames = new Map(); // instanceId → Map(oldId → newId)
  const anchorTargets = new Map(); // authoredId → [{instanceId, pageId, shared, newId}]

  // Pass 1: per-instance id inventory + classification.
  const inventory = [];
  for (const entry of entries) {
    const { section } = entry;
    if (typeof section.html !== 'string' || !section.html) continue;
    if (sectionUsesDocumentScope(section)) {
      inventory.push({ entry, ids: [], exempt: true });
      continue;
    }
    const wrapper = parseHTML(section.html);
    if (!wrapper) {
      findings.push({
        type: 'unparseable-section-html',
        severity: 'warning',
        instanceId: section.instanceId
      });
      inventory.push({ entry, ids: [], exempt: true });
      continue;
    }
    const declared = collectDeclaredIds(wrapper).filter(Boolean);
    const provenance = isValidIdProvenance(section.idProvenance) ? section.idProvenance : null;
    const owned = provenance ? new Set(provenance.sourceOwnedIds) : null;
    const ids = declared.map((id) => ({
      id,
      userAuthored: owned ? !owned.has(id) : false,
      legacy: !provenance
    }));
    if (!provenance && declared.length) {
      findings.push({
        type: 'legacy-id-provenance',
        severity: 'info',
        instanceId: section.instanceId,
        ids: declared
      });
    }
    inventory.push({ entry, ids, exempt: false });
  }

  const claimed = new Map(); // id → {instanceId, kind}
  const claim = (id, instanceId, kind) => {
    if (!claimed.has(id)) {
      claimed.set(id, { instanceId, kind });
      return true;
    }
    return false;
  };

  // Pass 2a: user-authored ids claim first, in document order.
  for (const { entry, ids, exempt } of inventory) {
    if (exempt) continue;
    for (const { id, userAuthored } of ids) {
      if (!userAuthored) continue;
      if (!claim(id, entry.section.instanceId, 'user')) {
        const holder = claimed.get(id);
        if (holder.kind === 'user' && holder.instanceId !== entry.section.instanceId) {
          findings.push({
            type: 'user-id-collision',
            severity: 'warning',
            id,
            instanceIds: [holder.instanceId, entry.section.instanceId]
          });
        }
      }
    }
  }

  // Pass 2b: legacy-provenance instances preserve (and claim) their ids.
  for (const { entry, ids, exempt } of inventory) {
    if (exempt) continue;
    for (const { id, legacy } of ids) {
      if (!legacy) continue;
      if (!claim(id, entry.section.instanceId, 'legacy')) {
        const holder = claimed.get(id);
        if (holder.instanceId !== entry.section.instanceId) {
          findings.push({
            type: 'legacy-id-duplicate',
            severity: 'warning',
            id,
            instanceIds: [holder.instanceId, entry.section.instanceId]
          });
        }
      }
    }
  }

  // Pass 2c: template-owned ids — first claimant keeps, later ones rename.
  for (const { entry, ids, exempt } of inventory) {
    if (exempt) continue;
    const { section } = entry;
    const rootExportId = exportIdMap.get(section.instanceId);
    for (const { id, userAuthored, legacy } of ids) {
      if (userAuthored || legacy) {
        recordAnchorTarget(anchorTargets, id, entry, id);
        continue;
      }
      if (claim(id, section.instanceId, 'template')) {
        recordAnchorTarget(anchorTargets, id, entry, id);
        continue;
      }
      if (claimed.get(id).instanceId === section.instanceId) {
        // Same instance declared the id twice — malformed markup; leave it.
        continue;
      }
      let candidate = `${rootExportId}-${id}`;
      let n = 2;
      while (claimed.has(candidate)) candidate = `${rootExportId}-${id}-${n++}`;
      claimed.set(candidate, { instanceId: section.instanceId, kind: 'renamed' });
      if (!descendantRenames.has(section.instanceId)) {
        descendantRenames.set(section.instanceId, new Map());
      }
      descendantRenames.get(section.instanceId).set(id, candidate);
      recordAnchorTarget(anchorTargets, id, entry, candidate);
    }
  }

  return { descendantRenames, anchorTargets };
}

function recordAnchorTarget(anchorTargets, authoredId, entry, finalId) {
  if (!anchorTargets.has(authoredId)) anchorTargets.set(authoredId, []);
  anchorTargets.get(authoredId).push({
    instanceId: entry.section.instanceId,
    pageId: entry.pageId,
    shared: entry.shared,
    newId: finalId
  });
}

/**
 * Build the full export id plan.
 *
 * @param {object} state
 * @param {object} [options]
 * @param {boolean} [options.canonicalIds=false]
 * @param {boolean} [options.normalizeTemplateIds=false] legacy-mode flag only
 * @param {(html: string) => Element|null} [options.parseHTML]
 * @returns {{exportIdMap: Map, descendantRenames: Map, anchorTargets: Map,
 *   findings: Array, canonicalIds: boolean}}
 */
export function buildExportIdPlan(state, options = {}) {
  const canonicalIds = options.canonicalIds === true;
  const normalizeTemplateIds = options.normalizeTemplateIds === true;
  const parseHTML = options.parseHTML || defaultParse;
  const entries = collectAllSectionInstances(state);
  const findings = [];

  const exportIdMap = canonicalIds
    ? assignCanonicalRootIds(entries)
    : assignLegacyRootIds(entries, normalizeTemplateIds);

  // Root export ids also claim into the anchor namespace: an old root id that
  // changes (e.g. `services-2152` → `services`) is itself an anchor target.
  let descendantRenames = new Map();
  let anchorTargets = new Map();
  if (canonicalIds) {
    ({ descendantRenames, anchorTargets } = computeDescendantPlan(
      entries,
      exportIdMap,
      parseHTML,
      findings
    ));
    for (const entry of entries) {
      const { section } = entry;
      const oldRootId = section.sectionId || section.catalogKey || section.instanceId;
      const newRootId = exportIdMap.get(section.instanceId);
      if (oldRootId && newRootId && oldRootId !== newRootId) {
        recordAnchorTarget(anchorTargets, oldRootId, entry, newRootId);
        // Anchors may also use the tpl-stripped form of the old root id.
        const stripped = normalizeTemplateSectionId(oldRootId);
        if (stripped && stripped !== oldRootId && stripped !== newRootId) {
          recordAnchorTarget(anchorTargets, stripped, entry, newRootId);
        }
      }
    }
  }

  return { exportIdMap, descendantRenames, anchorTargets, findings, canonicalIds };
}
